import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import ClaimLegalToDriveModal from './ClaimLegalToDriveModal'
import { FormCard, InfoAlert, SelectableCard } from './ui'
import { useApi } from '../services/api'
import type { PolicyLink } from '../services/types'
import { getPolicyType, type ClaimablePolicyType } from '../utils/policyType'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  CLAIM_FLOW_PATH,
  buildClaimIncidentHref,
  buildClaimOtherSubtypeHref,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  type ClaimEntryBranch,
} from '../claims/claimFlowModel'
import { readClaimDraft } from '../claims/claimDraftStorage'
import { readPolicyFromListCache } from '../utils/policiesListCache'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'
import {
  isLegalToDriveConfirmed,
  markLegalToDriveConfirmed,
} from '../claims/claimLegalToDriveGate'

export type { ClaimEntryBranch }

export interface ClaimEntryOption {
  branch: ClaimEntryBranch
  headline: string
  caption: string
}

/** First-step branch: windscreen/glass vs everything else (4x4). */
export const CLAIM_ENTRY_OPTIONS_4X4: ClaimEntryOption[] = [
  {
    branch: 'windscreen',
    headline: 'Windscreen or glass',
    caption: 'Cracked, chipped or shattered windscreen, side or rear window.',
  },
  {
    branch: 'other',
    headline: 'Any other type of claim',
    caption: "Any claim that isn't windscreen or glass only.",
  },
]

/** First-step branch: windscreen/glass vs everything else (caravan). */
export const CLAIM_ENTRY_OPTIONS_CARAVAN: ClaimEntryOption[] = [
  {
    branch: 'windscreen',
    headline: 'Windscreen or glass',
    caption: 'Cracked, chipped or shattered windscreen, side or rear window.',
  },
  {
    branch: 'other',
    headline: 'Any other type of claim',
    caption: "Any claim that isn't windscreen or glass only.",
  },
]

function isClaimablePolicyType(value: string | null): value is ClaimablePolicyType {
  return value === '4x4' || value === 'caravan' || value === 'camper' || value === 'unsupported'
}

export default function ClaimTypeSelection() {
  const navigate = useNavigate()
  const location = useLocation()
  const { getPolicies } = useApi()

  // Fast paths (no list fetch):
  //   1. ?type=4x4 / caravan / camper / unsupported in the URL
  //   2. location.state.policy (from pre-claim / other steps)
  //   3. Dashboard list cache in sessionStorage (same payload as getPolicies)
  // getPolicies is only a last resort (e.g. cold URL, cache cleared, policy not in list).
  const queryParams = new URLSearchParams(location.search)
  const policyRefFromQuery = decodePolicyRef(queryParams.get('policyRef'))
  const typeFromQueryRaw = queryParams.get('type')
  const typeFromQuery: ClaimablePolicyType | null = isClaimablePolicyType(typeFromQueryRaw)
    ? typeFromQueryRaw
    : null

  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null
  const fromListCache = readPolicyFromListCache(policyRefFromQuery)
  const bootstrapPolicy = navState?.policy ?? fromListCache

  const [policy, setPolicy] = useState<PolicyLink | null>(bootstrapPolicy)
  /** Safe-to-drive gate for direct URL entry (normally answered on PreClaim before this screen). */
  const [legalToDriveModalOpen, setLegalToDriveModalOpen] = useState(false)
  // Only show loading when we have no type hint, no state policy, and no list cache
  const [loading, setLoading] = useState(
    typeFromQuery === null && !navState?.policy && !fromListCache
  )
  const [fetchFailed, setFetchFailed] = useState(false)

  const policiesFetchErrorSignal = useMemo(
    () => (!loading && fetchFailed ? 'policies-fetch-failed' : null),
    [loading, fetchFailed]
  )
  useScrollToTopOnErrorSignal(policiesFetchErrorSignal)

  // Land at the top of the page on mount (consistent with the rest of the app).
  // Honour preserveScroll for callers that explicitly want to keep the position.
  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (typeFromQuery !== null || navState?.policy || fromListCache) return

    let cancelled = false

    const fetchPolicy = async () => {
      setLoading(true)
      setFetchFailed(false)
      try {
        const response = await getPolicies()
        if (cancelled) return
        if (!response.policies || response.policies.length === 0) {
          setPolicy(null)
          return
        }
        const matching = policyRefFromQuery
          ? response.policies.find((p) => p.policyRef === policyRefFromQuery)
          : response.policies[0]
        setPolicy((matching || response.policies[0]) as PolicyLink)
      } catch (error) {
        console.error('Error fetching policies for claim type selection:', error)
        if (!cancelled) {
          setPolicy(null)
          setFetchFailed(true)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchPolicy()
    return () => {
      cancelled = true
    }
  }, [getPolicies, policyRefFromQuery, fromListCache, typeFromQuery, navState?.policy])

  const targetPolicyRef = policy?.policyRef || policyRefFromQuery
  // URL hint wins over derived-from-policy so the page can render synchronously.
  const policyType: ClaimablePolicyType = typeFromQuery ?? getPolicyType(policy)

  const handleBack = () => {
    if (targetPolicyRef) {
      navigate(`${CLAIM_FLOW_PATH.start}?policyRef=${encodeURIComponent(targetPolicyRef)}`, {
        state: policy ? { policy } : undefined,
      })
    } else {
      navigate(CLAIM_FLOW_PATH.start)
    }
  }

  useEffect(() => {
    if (loading || fetchFailed || policyType === 'unsupported') return
    if (!isLegalToDriveConfirmed(targetPolicyRef ?? null)) {
      setLegalToDriveModalOpen(true)
    }
  }, [loading, fetchFailed, policyType, targetPolicyRef])

  const handleSelectEntryOption = (option: ClaimEntryOption) => {
    if (!isLegalToDriveConfirmed(targetPolicyRef ?? null)) {
      setLegalToDriveModalOpen(true)
      return
    }
    const typeHint =
      policyType === '4x4' || policyType === 'caravan' || policyType === 'camper' ? policyType : null
    if (option.branch === 'other') {
      navigate(buildClaimOtherSubtypeHref(targetPolicyRef ?? null, typeHint), {
        state: policy ? { policy } : undefined,
      })
      return
    }
    navigate(buildClaimIncidentHref(targetPolicyRef ?? null, typeHint, 'windscreen'), {
      state: policy ? { policy } : undefined,
    })
  }

  const handleLegalToDriveConfirm = () => {
    markLegalToDriveConfirmed(targetPolicyRef ?? null)
    setLegalToDriveModalOpen(false)
  }

  const handleLegalToDriveClose = () => {
    setLegalToDriveModalOpen(false)
    if (!isLegalToDriveConfirmed(targetPolicyRef ?? null)) {
      handleBack()
    }
  }

  const handleVehicleNotSafeToDrive = () => {
    setLegalToDriveModalOpen(false)
    const typeHint =
      policyType === '4x4' || policyType === 'caravan' || policyType === 'camper' ? policyType : null
    navigate('/claims/call-for-assistance', {
      state: {
        policy: policy ?? undefined,
        policyRef: targetPolicyRef ?? null,
        typeHint,
      },
    })
  }

  useEffect(() => {
    if (!loading && !fetchFailed && policy && policyType === 'unsupported') {
      if (import.meta.env.DEV) {
        console.warn(
          'Unsupported policy type for online claim:',
          policy.productRef ?? '(no productRef)'
        )
      }
    }
  }, [loading, fetchFailed, policy, policyType])

  const entryOptions =
    policyType === '4x4'
      ? CLAIM_ENTRY_OPTIONS_4X4
      : policyType === 'caravan' || policyType === 'camper'
        ? CLAIM_ENTRY_OPTIONS_CARAVAN
        : []

  const showEntryOptions = !loading && !fetchFailed && entryOptions.length > 0
  const showUnsupported = !loading && !fetchFailed && policyType === 'unsupported'
  const showError = !loading && fetchFailed

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={handleBack}
        title="Tell us what happened"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={formatClaimPolicySummaryLine(
              policy ?? (targetPolicyRef ? { policyRef: targetPolicyRef } : null)
            )}
            incidentSummary={formatIncidentRailSummary(readClaimDraft())}
          />
        }
      >
        <FormCard>
          <div className="flex flex-col gap-[20px] items-start w-full">
            <h2
              id="what-happened-heading"
              className="font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
            >
              What happened?
            </h2>

            {loading && (
              <div
                role="status"
                aria-live="polite"
                className="flex flex-col gap-[12px] w-full"
              >
                {Array.from({ length: CLAIM_ENTRY_OPTIONS_4X4.length }).map((_, idx) => (
                  <div
                    key={idx}
                    className="bg-background-tertiary border border-border-primary border-solid rounded-2xl w-full min-h-[104px] md:min-h-[120px] animate-pulse"
                  />
                ))}
                <span className="sr-only">Loading claim options</span>
              </div>
            )}

            {showError && (
              <InfoAlert
                iconColor="warning"
                message="We couldn't load your policy"
                description="Please refresh the page and try again. If the problem continues, call us on 1300 296 296."
              />
            )}

            {showUnsupported && (
              <InfoAlert
                iconColor="warning"
                message="Online claims aren't available for this policy yet"
                description="Please call us on 1300 296 296 and our team will help lodge your claim over the phone."
              />
            )}

            {showEntryOptions && (
              <ul
                aria-labelledby="what-happened-heading"
                className="flex flex-col gap-[12px] md:gap-[16px] w-full list-none p-0 m-0"
              >
                {entryOptions.map((option) => (
                  <li key={option.branch} className="w-full">
                    <SelectableCard
                      prominent
                      headline={option.headline}
                      caption={option.caption}
                      onClick={() => handleSelectEntryOption(option)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </FormCard>
      </ClaimsFlowMain>

      {legalToDriveModalOpen ? (
        <ClaimLegalToDriveModal
          onClose={handleLegalToDriveClose}
          onConfirm={handleLegalToDriveConfirm}
          onVehicleNotSafe={handleVehicleNotSafeToDrive}
        />
      ) : null}

      <Footer />
    </div>
  )
}
